import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Building2, Plus, X, Users, Zap, Loader2, Mail, Lock, User, Pencil, Trash2, Check
} from "lucide-react";

const AdminCorporate = () => {
  const { token } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    organization_name: "", admin_name: "", admin_email: "", admin_password: ""
  });

  const fetchAccounts = async () => {
    try {
      const res = await fetch(`${API}/admin/corporate/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAccounts(data.accounts || []);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAccounts(); }, [token]); // eslint-disable-line

  const handleCreate = async () => {
    if (!form.organization_name || !form.admin_name || !form.admin_email || !form.admin_password) {
      toast.error("All fields are required"); return;
    }
    if (form.admin_password.length < 6) {
      toast.error("Password must be at least 6 characters"); return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/admin/corporate/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(`Corporate account created! Credentials sent to ${form.admin_email}`);
        setShowCreate(false);
        setForm({ organization_name: "", admin_name: "", admin_email: "", admin_password: "" });
        fetchAccounts();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create account");
      }
    } catch (err) { toast.error("Error creating account"); }
    finally { setCreating(false); }
  };

  const startEdit = (acc) => {
    setEditingId(acc.user_id);
    setEditForm({
      organization_name: acc.organization_name || acc.name,
      admin_name: acc.name,
      admin_email: acc.email
    });
  };

  const handleUpdate = async (userId) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/corporate/${userId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(editForm)
      });
      if (res.ok) {
        toast.success("Account updated");
        setEditingId(null);
        fetchAccounts();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update");
      }
    } catch (err) { toast.error("Error updating account"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (userId, orgName) => {
    if (!window.confirm(`Delete "${orgName}" and all associated employees, programs, and data? This cannot be undone.`)) return;
    try {
      const res = await fetch(`${API}/admin/corporate/${userId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success("Corporate account deleted");
        fetchAccounts();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete");
      }
    } catch (err) { toast.error("Error deleting account"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-secondary" data-testid="corporate-admin-title">Corporate Wellness</h2>
          <p className="text-sm text-muted-foreground">Onboard and manage corporate wellness accounts</p>
        </div>
        <Button onClick={() => setShowCreate(!showCreate)} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid="create-corporate-btn">
          {showCreate ? <X className="w-4 h-4 mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
          {showCreate ? "Cancel" : "Onboard Company"}
        </Button>
      </div>

      {showCreate && (
        <Card className="rounded-2xl border-emerald-200 p-5 space-y-4" data-testid="create-corporate-form">
          <h3 className="font-bold text-secondary">New Corporate Wellness Account</h3>
          <p className="text-sm text-muted-foreground">The admin will receive an email with their login credentials.</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Organization Name</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="e.g. TechCorp, HealthFirst Inc." value={form.organization_name} onChange={(e) => setForm({ ...form, organization_name: e.target.value })} className="rounded-xl pl-10" data-testid="corp-org-name" />
              </div>
            </div>
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Admin Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input placeholder="e.g. Jane Smith" value={form.admin_name} onChange={(e) => setForm({ ...form, admin_name: e.target.value })} className="rounded-xl pl-10" data-testid="corp-admin-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Admin Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="email" placeholder="admin@company.com" value={form.admin_email} onChange={(e) => setForm({ ...form, admin_email: e.target.value })} className="rounded-xl pl-10 text-sm" data-testid="corp-admin-email" />
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Temporary Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input type="text" placeholder="Min 6 characters" value={form.admin_password} onChange={(e) => setForm({ ...form, admin_password: e.target.value })} className="rounded-xl pl-10 text-sm" data-testid="corp-admin-password" />
                </div>
              </div>
            </div>
          </div>
          <Button onClick={handleCreate} disabled={creating} className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid="submit-corporate-btn">
            {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Building2 className="w-4 h-4 mr-2" />}
            Create Corporate Account & Send Credentials
          </Button>
        </Card>
      )}

      {loading ? (
        <div className="space-y-4">{[1, 2].map((i) => <div key={i} className="animate-pulse h-24 bg-white rounded-2xl" />)}</div>
      ) : accounts.length === 0 ? (
        <Card className="rounded-2xl p-8 text-center border-emerald-100">
          <Building2 className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
          <p className="text-muted-foreground">No corporate accounts yet. Onboard your first company!</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {accounts.map((acc) => (
            <Card key={acc.user_id} className="rounded-2xl border-emerald-100/50" data-testid={`corporate-account-${acc.user_id}`}>
              <CardContent className="p-5">
                {editingId === acc.user_id ? (
                  /* ===== EDIT MODE ===== */
                  <div className="space-y-3">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-bold text-secondary text-sm">Edit Corporate Account</h3>
                      <Button variant="ghost" size="sm" onClick={() => setEditingId(null)} className="text-muted-foreground"><X className="w-4 h-4" /></Button>
                    </div>
                    <div>
                      <label className="text-xs text-muted-foreground mb-1 block">Organization Name</label>
                      <Input value={editForm.organization_name} onChange={(e) => setEditForm({ ...editForm, organization_name: e.target.value })} className="rounded-xl" data-testid="edit-org-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Admin Name</label>
                        <Input value={editForm.admin_name} onChange={(e) => setEditForm({ ...editForm, admin_name: e.target.value })} className="rounded-xl" data-testid="edit-admin-name" />
                      </div>
                      <div>
                        <label className="text-xs text-muted-foreground mb-1 block">Admin Email</label>
                        <Input value={editForm.admin_email} onChange={(e) => setEditForm({ ...editForm, admin_email: e.target.value })} className="rounded-xl" data-testid="edit-admin-email" />
                      </div>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={() => handleUpdate(acc.user_id)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl" data-testid="save-edit-btn">
                        {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />} Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setEditingId(null)} className="rounded-xl">Cancel</Button>
                    </div>
                  </div>
                ) : (
                  /* ===== VIEW MODE ===== */
                  <div>
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="space-y-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Building2 className="w-5 h-5 text-emerald-600 shrink-0" />
                          <h3 className="font-bold text-secondary truncate">{acc.organization_name || acc.name}</h3>
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs">Corporate</Badge>
                        </div>
                        <p className="text-xs sm:text-sm text-muted-foreground truncate">{acc.name} — {acc.email}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <div className="flex gap-3 sm:gap-4 text-center text-xs sm:text-sm mr-2 sm:mr-4">
                          <div>
                            <p className="font-bold text-secondary flex items-center gap-1"><Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-500" />{acc.employee_count}</p>
                            <p className="text-[10px] text-muted-foreground">Employees</p>
                          </div>
                          <div>
                            <p className="font-bold text-secondary flex items-center gap-1"><Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-orange-500" />{acc.program_count}</p>
                            <p className="text-[10px] text-muted-foreground">Programs</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => startEdit(acc)} className="text-muted-foreground hover:text-emerald-600" data-testid={`edit-corporate-${acc.user_id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(acc.user_id, acc.organization_name || acc.name)} className="text-muted-foreground hover:text-red-500" data-testid={`delete-corporate-${acc.user_id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">Joined {new Date(acc.created_at).toLocaleDateString()}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCorporate;
